/**
 * SendQuoteRequestDialog — modal d'envoi d'une quote request (Chunk 9.L.6).
 *
 * Backend : `POST /v1/quote-requests/:id/send` (9.L.1).
 * Le broker peut envoyer (status=draft) ou relancer (status=sent, idempotent
 * partial-send retry sur recipients pending uniquement, déjà-sent skipped).
 *
 * UX :
 *   - Affiche un résumé QR (title + count recipients pending vs déjà-sent)
 *     fetché via `useQuoteRequest` (nested recipients).
 *   - Date de réponse attendue — **obligatoire pour envoyer** (9.AQ.1). C'est elle
 *     qui déclenche les rappels : partir sans elle, c'est partir avec les relances
 *     silencieusement désactivées. Sur une relance, la date déjà fixée suffit et le
 *     champ peut rester vide ; l'écran affiche alors celle qui s'appliquera.
 *     Aucune date par défaut n'est proposée — le conseiller la choisit.
 *   - Esc + backdrop click → close (sauf pendant submit).
 *
 * Erreurs API (bilingues via `getApiErrorMessage`) :
 *   - 404 QUOTE_REQUESTS_NOT_FOUND     → bandeau red + invalidate cache parent
 *   - 409 QUOTE_REQUESTS_INVALID_TRANSITION → toast info + invalidate + close
 *     (race condition : status passé à un terminal entre listing et clic)
 *   - 422 QUOTE_REQUESTS_NO_RECIPIENTS → bandeau red explicite
 *   - 500 QUOTE_REQUESTS_EMAIL_SEND_FAILED → bandeau red avec suggestion retry
 *   - 403 GATING_HUMAN_REQUIRED       → bandeau red (broker JWT bypass naturel,
 *     rare en pratique humaine — apparaîtrait via api_key sans override)
 *   - 400 VALIDATION_ERROR             → erreur inline sur le date input
 *
 * Pattern parité `ResolveEventDialog` (9.G.2) : role=dialog + aria-modal +
 * aria-labelledby + focus initial sur Cancel + touch-manipulation.
 */

import type { QuoteRequestRow } from "@kollectivia/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useQuoteRequest, useSendQuoteRequest } from "../../../lib/queries/quoteRequests";
import { toast } from "../../../lib/toast";

import { RecipientStatusBadge } from "./RecipientStatusBadge";

type Props = {
  readonly quoteRequest: QuoteRequestRow;
  readonly onClose: () => void;
};

/** Convert ISO date input value (YYYY-MM-DD) to ISO datetime at noon UTC. */
function dateInputToIsoDatetime(value: string): string | undefined {
  if (!value) return undefined;
  return `${value}T12:00:00.000Z`;
}

export function SendQuoteRequestDialog({ quoteRequest, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const descId = useId();
  const dateFieldId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const queryClient = useQueryClient();

  // Refetch QR detail pour afficher recipients à jour (status + count).
  const qrDetail = useQuoteRequest(quoteRequest.id);
  const send = useSendQuoteRequest();
  const { reset: resetMutation } = send;

  const [expectedResponseDate, setExpectedResponseDate] = useState<string>("");

  // Reset à chaque ouverture + focus initial Cancel.
  useEffect(() => {
    resetMutation();
    setExpectedResponseDate("");
    queueMicrotask(() => cancelBtnRef.current?.focus());
  }, [resetMutation]);

  // Esc → close (sauf pendant submit).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !send.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, send.isPending]);

  // 9.AQ.1 — la date qui S'APPLIQUERA : celle saisie, sinon celle déjà posée sur
  // la demande. Même recouvrement que le serveur ; l'écran ne doit pas annoncer
  // une échéance différente de celle qui sera enregistrée.
  const dateEffective =
    dateInputToIsoDatetime(expectedResponseDate) ?? quoteRequest.expected_response_by ?? null;
  const dateFmt = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? "fr", { dateStyle: "long" });

  const recipients = qrDetail.data?.recipients ?? [];
  const pendingCount = useMemo(
    () => recipients.filter((r) => r.status === "pending").length,
    [recipients],
  );
  const sentCount = useMemo(
    () => recipients.filter((r) => r.status === "sent").length,
    [recipients],
  );

  const onConfirm = () => {
    // Garde d'écran seulement : le refus qui compte est celui du serveur, sur
    // tous les chemins d'envoi. Ici on évite juste un aller-retour perdu.
    if (dateEffective === null) return;
    const isoDatetime = dateInputToIsoDatetime(expectedResponseDate);
    send.mutate(
      {
        quoteRequestId: quoteRequest.id,
        input: isoDatetime ? { expected_response_by: isoDatetime } : undefined,
        projectId: quoteRequest.project_id,
      },
      {
        onSuccess: () => {
          toast.success(t("quote_requests.send_dialog.success"));
          onClose();
        },
        onError: (err) => {
          // 409 race : autre acteur a transitionné → invalidate + toast + close.
          if (err instanceof ApiException && err.status === 409) {
            void queryClient.invalidateQueries({
              queryKey: ["quote-requests"],
            });
            toast.info(t("quote_requests.send_dialog.race_lost"));
            onClose();
          }
          // 400/404/422/500/403 → bandeau inline (via send.error).
        },
      },
    );
  };

  const apiErrorMsg =
    send.error instanceof ApiException && send.error.status !== 409
      ? getApiErrorMessage(send.error, lang) || t("errors.generic")
      : send.isError && !(send.error instanceof ApiException && send.error.status === 409)
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!send.isPending) onClose();
        }}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("quote_requests.send_dialog.title")}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <p id={descId} className="text-sm text-slate-700">
            {quoteRequest.title?.trim() || t("quote_requests.send_dialog.untitled")}
          </p>

          {qrDetail.isLoading ? (
            <p className="text-sm text-slate-400">{t("app.loading")}</p>
          ) : qrDetail.isError ? (
            <p className="text-sm text-red-700">
              {t("quote_requests.send_dialog.recipients_error")}
            </p>
          ) : recipients.length === 0 ? (
            <p className="text-sm text-amber-700">
              {t("quote_requests.send_dialog.no_recipients_warning")}
            </p>
          ) : (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-xs font-medium text-slate-600">
                {t("quote_requests.send_dialog.recipients_summary", {
                  pending: pendingCount,
                  sent: sentCount,
                })}
              </p>
              <ul className="mt-2 space-y-1">
                {recipients.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center justify-between gap-2 text-xs text-slate-700"
                  >
                    <span className="truncate font-mono">
                      {r.insurer_code}
                      {r.recipient_email ? (
                        <span className="ml-2 text-slate-500">{r.recipient_email}</span>
                      ) : null}
                    </span>
                    <RecipientStatusBadge status={r.status} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <label
              htmlFor={dateFieldId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("quote_requests.send_dialog.expected_response_by_label")}
            </label>
            <input
              id={dateFieldId}
              type="date"
              value={expectedResponseDate}
              onChange={(e) => setExpectedResponseDate(e.target.value)}
              disabled={send.isPending}
              required={quoteRequest.expected_response_by === null}
              aria-describedby={`${dateFieldId}-hint`}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
            />
            <p id={`${dateFieldId}-hint`} className="mt-1.5 text-xs text-slate-500">
              {t("quote_requests.send_dialog.expected_response_by_hint")}
            </p>
            {/* Ce que le conseiller obtiendra réellement — ou pourquoi il ne peut
                pas encore envoyer. Un bouton grisé sans explication est un échec
                silencieux. */}
            {dateEffective ? (
              <p className="mt-1.5 text-sm font-medium text-slate-800">
                {t("quote_requests.send_dialog.expected_response_by_effective", {
                  date: dateFmt.format(new Date(dateEffective)),
                })}
              </p>
            ) : (
              <p className="mt-1.5 text-sm text-amber-800">
                {t("quote_requests.send_dialog.expected_response_by_required")}
              </p>
            )}
          </div>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={send.isPending}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={send.isPending || dateEffective === null}
            aria-busy={send.isPending}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {send.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {send.isPending
              ? t("quote_requests.send_dialog.submitting")
              : t("quote_requests.send_dialog.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
